import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { apiService, AssetTag, PolicyTag } from '../services/api';
import { XMarkIcon, PlusIcon, TagIcon } from '@heroicons/react/24/outline';

interface TagManagerProps {
  resourceType: 'asset' | 'policy';
  resourceId: string;
  onTagsChange?: () => void;
  /** called after every fetch with the current tag count */
  onTagCountChange?: (count: number) => void;
  compact?: boolean;
  /** compact only — hide the chips row */
  showChips?: boolean;
  /** compact only — hide the text input */
  showInput?: boolean;
}

const TagManager: React.FC<TagManagerProps> = ({
  resourceType, resourceId, onTagsChange, onTagCountChange,
  compact = false,
  showChips = true,
  showInput = true,
}) => {
  const [tags, setTags] = useState<(AssetTag | PolicyTag)[]>([]);
  const [loading, setLoading] = useState(true);
  const [newTagInput, setNewTagInput] = useState('');
  const [searchResults, setSearchResults] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  // Fetch existing tags for the resource
  const fetchTags = useCallback(async () => {
    try {
      setLoading(true);
      let fetched: (AssetTag | PolicyTag)[];
      if (resourceType === 'asset') {
        const response = await apiService.getAssetTags(resourceId);
        fetched = response.tags;
      } else {
        const response = await apiService.getPolicyTags(resourceId);
        fetched = response.tags;
      }
      setTags(fetched);
      onTagCountChange?.(fetched.length);
    } catch (error: any) {
      console.error('Error fetching tags:', error);
      toast.error('Failed to load tags');
    } finally {
      setLoading(false);
    }
  }, [resourceType, resourceId, onTagCountChange]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  // Search tags for autocomplete
  const handleSearchTags = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await apiService.searchTags(query, 10);
      setSearchResults(response.tags);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error searching tags:', error);
      setSearchResults([]);
    }
  }, []);

  // Add a tag or multiple tags (comma-separated)
  const handleAddTag = async (tagInput: string) => {
    // Remove trailing commas and split by comma
    const cleanedInput = tagInput.replace(/,\s*$/, '').trim();
    if (!cleanedInput) return;

    // Split by comma, trim, and filter out empty strings
    const tagNames = cleanedInput
      .split(',')
      .map(t => t.trim())
      .filter(t => t);

    if (tagNames.length === 0) return;

    // Validate all tag names (alphanumeric, hyphens, underscores only)
    const validTagPattern = /^[a-zA-Z0-9_-]+$/;
    const invalidTags = tagNames.filter(tag => !validTagPattern.test(tag));
    if (invalidTags.length > 0) {
      toast.error('Tag names can only contain letters, numbers, hyphens, and underscores');
      return;
    }

    // Check for duplicate tags (case-insensitive)
    const normalizedExistingTags = new Set(tags.map(t => t.tag_name.toLowerCase()));
    const duplicateTags = tagNames.filter(tag => 
      normalizedExistingTags.has(tag.toLowerCase())
    );
    
    // Filter out duplicates from input
    const newTagsToAdd = Array.from(new Set(tagNames)).filter(tag => 
      !normalizedExistingTags.has(tag.toLowerCase())
    );

    if (newTagsToAdd.length === 0) {
      if (duplicateTags.length > 0) {
        toast.error('All tags are already added');
      }
      return;
    }

    setIsAdding(true);
    try {
      let successCount = 0;
      let errorCount = 0;

      // Add all tags sequentially
      for (const tagName of newTagsToAdd) {
        try {
          if (resourceType === 'asset') {
            await apiService.addAssetTag(resourceId, {
              tag_name: tagName,
              tag_type: 'user'
            });
          } else {
            await apiService.addPolicyTag(resourceId, {
              tag_name: tagName,
              tag_type: 'user'
            });
          }
          successCount++;
        } catch (error: any) {
          console.error(`Error adding tag ${tagName}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(
          `${successCount} tag${successCount > 1 ? 's' : ''} added successfully${duplicateTags.length > 0 ? ` (${duplicateTags.length} already existed)` : ''}`,
          { duration: 3000 }
        );
      }
      
      if (errorCount > 0) {
        toast.error(`Failed to add ${errorCount} tag${errorCount > 1 ? 's' : ''}`, { duration: 3000 });
      }

      setNewTagInput('');
      setShowSuggestions(false);
      setSearchResults([]);
      await fetchTags();
      onTagsChange?.();
    } catch (error: any) {
      console.error('Error adding tags:', error);
      const errorMessage = error.response?.data?.error || 'Failed to add tags';
      toast.error(errorMessage);
    } finally {
      setIsAdding(false);
    }
  };

  // Remove a tag
  const handleRemoveTag = async (tagName: string) => {
    try {
      if (resourceType === 'asset') {
        await apiService.removeAssetTag(resourceId, tagName);
      } else {
        await apiService.removePolicyTag(resourceId, tagName);
      }
      
      toast.success('Tag removed successfully');
      await fetchTags();
      onTagsChange?.();
    } catch (error: any) {
      console.error('Error removing tag:', error);
      toast.error(error.response?.data?.error || 'Failed to remove tag');
    }
  };

  // Handle input change with debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (newTagInput.trim()) {
        handleSearchTags(newTagInput);
      } else {
        setSearchResults([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [newTagInput, handleSearchTags]);

  const handleInputChange = (value: string) => {
    setNewTagInput(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newTagInput.trim()) {
      e.preventDefault();
      handleAddTag(newTagInput);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  if (loading && compact && !showInput) {
    return <div className="animate-pulse h-5 w-24 bg-gray-100 rounded-md" />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {/* Chips */}
        {showChips && tags.map((tag) => (
          <span
            key={tag.id}
            className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 rounded-md px-2 py-0.5"
          >
            <span className="text-xs text-blue-700">{tag.tag_name}</span>
            <button
              onClick={() => handleRemoveTag(tag.tag_name)}
              className="text-blue-300 hover:text-red-500 transition-colors"
              title="Remove tag"
            >
              <XMarkIcon className="w-3 h-3" />
            </button>
          </span>
        ))}

        {/* Input */}
        {showInput && (
          <div className="relative">
            <input
              type="text"
              value={newTagInput}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => { if (searchResults.length > 0) setShowSuggestions(true); }}
              placeholder={showChips && tags.length > 0 ? '+ tag' : 'Add tags…'}
              title="Type tag name and press Enter. Comma-separate to add multiple."
              className="text-xs border border-dashed border-gray-300 rounded-md px-2 py-0.5 w-16 focus:w-32 focus:border-solid focus:border-governate-400 focus:outline-none bg-transparent placeholder-gray-400 transition-all duration-150"
              disabled={isAdding}
            />
            {showSuggestions && searchResults.length > 0 && (
              <div className="absolute z-10 top-full mt-1 left-0 w-40 bg-white border border-gray-200 rounded-lg shadow-lg max-h-32 overflow-y-auto">
                {searchResults.map((tagName, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setNewTagInput(tagName);
                      setShowSuggestions(false);
                      handleAddTag(tagName);
                    }}
                    className="w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-100 flex items-center gap-1.5"
                  >
                    <TagIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
                    <span>{tagName}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Current Tags */}
      <div>
        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Current Tags
        </label>
        {tags.length === 0 ? (
          <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">No tags added yet</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <div
                key={tag.id}
                className="inline-flex items-center space-x-1.5 md:space-x-2 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg px-2 md:px-3 py-1 md:py-1.5 group"
              >
                <TagIcon className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                <span className="text-xs md:text-sm text-blue-700 dark:text-blue-300">{tag.tag_name}</span>
                <button
                  onClick={() => handleRemoveTag(tag.tag_name)}
                  className="text-blue-400 dark:text-blue-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  title="Remove tag"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add New Tag */}
      <div className="relative">
        <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Add New Tag
        </label>
        <div className="flex space-x-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={newTagInput}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (searchResults.length > 0) {
                  setShowSuggestions(true);
                }
              }}
              placeholder="Type to search or create a tag..."
              className="w-full px-2 md:px-3 py-1.5 md:py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-governate-500 focus:border-transparent"
              disabled={isAdding}
            />
            
            {/* Autocomplete Suggestions */}
            {showSuggestions && searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {searchResults.map((tagName, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setNewTagInput(tagName);
                      setShowSuggestions(false);
                      handleAddTag(tagName);
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                  >
                    <TagIcon className="w-4 h-4 text-gray-400" />
                    <span>{tagName}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <button
            onClick={() => handleAddTag(newTagInput)}
            disabled={!newTagInput.trim() || isAdding}
            className="px-3 md:px-4 py-1.5 md:py-2 border-2 border-black dark:border-white text-black dark:text-white text-xs md:text-sm font-medium bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-1.5 md:space-x-2 rounded-lg"
            title="Add tag"
          >
            {isAdding ? (
              <div className="animate-spin rounded-full h-3.5 w-3.5 md:h-4 md:w-4 border-b-2 border-current"></div>
            ) : (
              <PlusIcon className="w-3.5 h-3.5 md:w-4 md:h-4" />
            )}
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Press Enter or click + to add. Use commas to add multiple tags at once (e.g., "financial, legal, confidential"). Only letters, numbers, hyphens, and underscores allowed.
        </p>
      </div>
    </div>
  );
};

export default TagManager;

